import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch, API } from '../api';
import { deliverLabel, detectZebraIfEnabled } from '../labelPrint';

// The storage-tier card on the product page (docs/STORAGE-TIER.md "Where it shows"). One read
// (GET /api/storage/style?wms_id=) brings the style's current verdict with the numbers that made it: the tier,
// the sentence, cover, need, boxes should / has, the flags with their numbers, the sizes. A manager (role or the
// `storage_manage` right) can pin the tier with a reason, set the cover months, write a note — every override is
// logged and the style is recomputed at once. The box label prints through the same Zebra path as WH Labels.
// Advisory only: nothing here moves stock or writes a shelf. English — a manager's tool. -- Claude, 2026-10-31

export interface TierStyle {
  style_id: number; webshop_id: string; display_sku: string | null; name: string | null; family: string | null; garment_class: string | null;
  colour: string | null; is_nos: boolean; is_managed: boolean; pinned_tier: string | null; pinned_reason: string | null; pinned_by: string | null;
  cover_months_set: number | null; cover_set_by: string | null;
  tier: string; tier_source: string; tier_computed: string | null; sold_12m: number; sold_3m: number; peak3_units: number; peak_from: string | null; peak_to: string | null;
  season_factor: number; cover_months: number; cover_source: string; cover_receipts: number; need_total: number; need_max_size: number; live_sizes: number;
  stock_total: number; stock_boxes: number; box_kind: string; cap: number; boxes_should: number; boxes_has: number; sections_should: number;
  waists: any; codes: { code: string; display: string; face: string | null; box_type: string | null; pieces: number; boxes: number; counted: boolean; why?: string | null }[];
  home_faces: string[]; flags: string[]; flag_detail: Record<string, any>; months: { from: string; units: number[] }; history_months: number; sentence: string; as_of: string;
  wms_id?: number | null;
}
interface SizeRow { size_label: string; sold_12m: number; stock_qty: number; need: number; boxes_need: number; waist: number | null; length_cls: string | null }
interface Ev { id: number; at: string; who: string | null; field: string; old_value: string | null; new_value: string | null; reason: string | null }
interface Hist { run_date: string; tier: string; tier_source: string; boxes_should: number; sold_12m: number; need_total: number; cover_months: number }
interface Finding { id: number; kind: string; severity: string; code: string | null; numbers: any; text: string | null }
interface Reply { found: boolean; style?: TierStyle | null; sizes?: SizeRow[]; events?: Ev[]; history?: Hist[]; findings?: Finding[]; can_manage?: boolean; note?: string; result?: null }

export const TIER_COLORS: Record<string, string> = {
  A: 'bg-emerald-600 text-white', B: 'bg-blue-600 text-white', C: 'bg-gray-500 text-white',
  J1: 'bg-purple-600 text-white', J2: 'bg-purple-700 text-white', J3: 'bg-purple-800 text-white',
  S: 'bg-amber-500 text-white', '0': 'bg-gray-300 text-gray-800',
};
export const TIER_WORDS: Record<string, string> = {
  A: 'one box per size', B: 'two sizes per box', C: 'as few boxes as it needs', J1: 'jeans: S, R and L together',
  J2: 'jeans: S apart', J3: 'jeans: S, R and L apart', S: 'supplier stock', '0': 'dead',
};
export const FLAG_WORDS: Record<string, string> = {
  low_season: 'low season now — do not shrink', overstock: 'more on hand than it should', under_room: 'less room than it should have',
  off_home_face: 'not on its home face', colour_clash: 'same colour in one stack', new_style: 'new style', empty_shelf_kept: 'sold out, shelf kept',
  dead_size: 'dead size', negative_stock: 'negative stock in the shop', class_unknown: 'garment class unknown', colour_unknown: 'colour unknown',
  family_unknown: 'family unknown', c_style_inbounded_weekly: 'C by the numbers, inbounded weekly', a_style_no_sales: 'pinned A, nothing sold',
  stock_on_special: 'stock on Ä/Ö or a code not in the plan', siblings_merged: 'sibling SKUs merged',
};

export function TierBadge({ tier, size = 'md' }: { tier: string | null | undefined; size?: 'sm' | 'md' | 'lg' }) {
  if (!tier) return null;
  const cls = TIER_COLORS[tier] || 'bg-gray-400 text-white';
  const dim = size === 'lg' ? 'text-2xl px-3 py-1' : size === 'sm' ? 'text-xs px-1.5 py-0.5' : 'text-sm px-2 py-0.5';
  return <span className={`inline-block rounded font-bold ${dim} ${cls}`} title={TIER_WORDS[tier] || ''}>{tier}</span>;
}

/** the numbers of one flag as a short readable string */
export function flagNumbers(k: string, d: any): string {
  if (d == null) return '';
  if (Array.isArray(d)) return d.map(x => typeof x === 'object' ? Object.values(x).filter(v => v != null && typeof v !== 'object').join(' ') : String(x)).join('; ');
  if (typeof d === 'object') return Object.entries(d).filter(([, v]) => v != null && typeof v !== 'object').map(([kk, v]) => `${kk.replace(/_/g, ' ')} ${v}`).join(' · ');
  return String(d);
}

const input = 'bg-white border border-[#9aa0a6] text-[#111111] rounded px-2 py-1.5 text-sm';
const btn = 'px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-40';

export default function StorageTierCard({ wmsId }: { wmsId: number | null }) {
  const navigate = useNavigate();
  const [r, setR] = useState<Reply | null>(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState<'sizes' | 'override' | 'label' | 'log' | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  // override drafts
  const [pin, setPin] = useState(''); const [reason, setReason] = useState(''); const [cover, setCover] = useState(''); const [note, setNote] = useState('');
  const [copies, setCopies] = useState(1);

  const load = useCallback(async () => {
    if (!wmsId) { setR({ found: false }); return; }
    setErr('');
    try {
      const res = await authFetch(`${API}/storage/style?wms_id=${wmsId}`);
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || `HTTP ${res.status}`);
      setR(d);
      const s = d.style as TierStyle | undefined;
      if (s) { setPin(s.pinned_tier || ''); setReason(s.pinned_reason || ''); setCover(s.cover_months_set != null ? String(s.cover_months_set) : ''); setCopies(Math.max(1, Math.min(50, s.boxes_should || 1))); }
    } catch (e: any) { setErr(e.message || 'Could not load the storage tier'); }
  }, [wmsId]);
  useEffect(() => { load(); }, [load]);

  if (!r && !err) return null;
  if (err) return <div className="mt-3 bg-white rounded-xl border border-[#d1d5db] p-3 text-sm text-[#5f6368]">Storage tier: {err}</div>;
  if (!r || !r.found || !r.style) {
    return (
      <div className="mt-3 bg-white rounded-xl border border-[#d1d5db] p-3 text-sm text-[#5f6368]">
        <span className="font-semibold text-[#111111]">Storage tier</span> — {r?.note ? r.note : 'no style for this product yet (Big Oliver rows mirror their Motley twin; a new article gets its style at the nightly run).'}
      </div>
    );
  }
  const s = r.style;
  const can = !!r.can_manage;
  const peak = s.peak_from && s.peak_to ? `${s.peak_from}–${s.peak_to}` : '—';
  const coverTxt = `${s.cover_months} months (${s.cover_source === 'measured' ? `measured, ${s.cover_receipts} receipts` : s.cover_source === 'manual' ? `set by ${s.cover_set_by || '?'}` : 'fallback'})`;
  const kindCls = [s.family, s.garment_class, s.colour].filter(Boolean).join(' · ');

  const patch = async (body: any) => {
    setBusy(true); setMsg('');
    try {
      const res = await authFetch(`${API}/storage/style/${s.style_id}`, { method: 'PATCH', body: JSON.stringify(body) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : `HTTP ${res.status}`);
      setR(d); setMsg('✓ Saved and recomputed');
      const ns = d.style as TierStyle; setPin(ns.pinned_tier || ''); setReason(ns.pinned_reason || ''); setCover(ns.cover_months_set != null ? String(ns.cover_months_set) : '');
    } catch (e: any) { setMsg(e.message || 'Not saved'); } finally { setBusy(false); }
  };

  const printLabel = async () => {
    const win = window.open('', '_blank');           // opened in the click so the popup is allowed
    setBusy(true); setMsg('');
    try {
      const lines = [kindCls || s.family || '', `${s.display_sku || ''} ${s.name || ''}`.trim(),
        s.tier.startsWith('J') ? `sized for ${peak} · ${s.waists?.stacks || ''} SL stacks` : `sized for ${peak} · ${s.boxes_should} ${s.box_kind} box${s.boxes_should !== 1 ? 'es' : ''}`];
      const zebra = await detectZebraIfEnabled();
      const res = await authFetch(`${API}/wh-labels/print`, { method: 'POST', body: JSON.stringify({ format: zebra ? 'zpl' : 'pdf', labels: [{ kind: 'box', tier: s.tier, lines, copies }] }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : `HTTP ${res.status}`);
      const direct = await deliverLabel(win, d, zebra);
      setMsg(direct ? `✓ ${d.count} box label${d.count !== 1 ? 's' : ''} sent to the Zebra` : `${d.count} label${d.count !== 1 ? 's' : ''} in the print window`);
    } catch (e: any) { if (win) win.close(); setMsg(e.message || 'Could not print'); } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 bg-white rounded-xl border border-[#d1d5db] p-3">
      <div className="flex flex-wrap items-center gap-2">
        <TierBadge tier={s.tier} size="lg" />
        <div className="flex-1 min-w-[12rem]">
          <div className="font-semibold">Storage tier <span className="font-normal text-[#5f6368]">· {TIER_WORDS[s.tier] || ''}{s.tier_source !== 'computed' ? ` · ${s.tier_source}` : ''}</span></div>
          <div className="text-xs text-[#5f6368]">{kindCls}{s.is_managed ? '' : ' · not a managed family'}{s.as_of ? ` · as of ${s.as_of}` : ''}</div>
        </div>
        {can && <button onClick={() => navigate('/settings/warehouse/storage')} className="text-xs text-blue-700 underline">Storage tiers →</button>}
      </div>
      <div className="mt-2 text-sm leading-snug">{s.sentence}</div>

      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 text-xs">
        <div><span className="text-[#5f6368]">Sold 12 months</span><div className="font-semibold text-sm">{s.sold_12m}</div></div>
        <div><span className="text-[#5f6368]">Last 3 months</span><div className="font-semibold text-sm">{s.sold_3m}</div></div>
        <div><span className="text-[#5f6368]">Peak</span><div className="font-semibold text-sm">{peak} <span className="font-normal text-[#5f6368]">×{s.season_factor}</span></div></div>
        <div><span className="text-[#5f6368]">Cover</span><div className="font-semibold text-sm">{coverTxt}</div></div>
        <div><span className="text-[#5f6368]">Needs at the peak</span><div className="font-semibold text-sm">{s.need_total} pcs <span className="font-normal text-[#5f6368]">(biggest size {s.need_max_size})</span></div></div>
        <div><span className="text-[#5f6368]">Box</span><div className="font-semibold text-sm">{s.box_kind} <span className="font-normal text-[#5f6368]">· {s.cap} pcs per box</span></div></div>
        <div><span className="text-[#5f6368]">Boxes should / has</span><div className={`font-semibold text-sm ${s.boxes_has < s.boxes_should ? 'text-amber-700' : ''}`}>{s.boxes_should} / {s.boxes_has}</div></div>
        <div><span className="text-[#5f6368]">In stock now</span><div className="font-semibold text-sm">{s.stock_total} pcs <span className="font-normal text-[#5f6368]">≈ {s.stock_boxes} boxes</span></div></div>
      </div>

      {s.codes?.length > 0 && (
        <div className="mt-2 text-xs">
          <span className="text-[#5f6368]">Stands on </span>
          {s.codes.map(c => (
            <button key={c.code} onClick={() => navigate(`/warehouse/location/${encodeURIComponent(c.code)}`)}
              className={`inline-block mr-1.5 mb-1 px-1.5 py-0.5 rounded border ${c.counted ? 'border-[#d1d5db] bg-[#f3f4f6]' : 'border-amber-600 bg-amber-50'}`}
              title={c.why || (c.box_type ? `${c.box_type} stack` : '')}>
              <span className="font-mono font-semibold">{c.display}</span> <span className="text-[#5f6368]">{c.pieces} pcs ≈ {c.boxes} box{c.boxes !== 1 ? 'es' : ''}{c.counted ? '' : ' · not counted'}</span>
            </button>
          ))}
          {s.home_faces?.length > 0 && <span className="text-[#5f6368]">· home {s.home_faces.join(', ')}</span>}
        </div>
      )}

      {s.flags?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {s.flags.map(f => {
            const warn = ['under_room', 'off_home_face', 'overstock', 'colour_clash', 'negative_stock', 'c_style_inbounded_weekly', 'a_style_no_sales'].includes(f);
            return <span key={f} className={`text-xs px-2 py-0.5 rounded-full border ${warn ? 'bg-amber-50 border-amber-600 text-amber-900' : 'bg-[#f3f4f6] border-[#d1d5db] text-[#374151]'}`} title={flagNumbers(f, s.flag_detail?.[f])}>
              {FLAG_WORDS[f] || f.replace(/_/g, ' ')}{s.flag_detail?.[f] ? ` (${flagNumbers(f, s.flag_detail[f]).slice(0, 60)})` : ''}
            </span>;
          })}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2 items-center">
        <button onClick={() => setOpen(open === 'sizes' ? null : 'sizes')} className={`${btn} bg-[#dfe3e8] hover:bg-[#c6cdd5]`}>{open === 'sizes' ? 'Hide sizes' : `Sizes (${r.sizes?.length || 0})`}</button>
        {can && <button onClick={() => setOpen(open === 'override' ? null : 'override')} className={`${btn} bg-[#dfe3e8] hover:bg-[#c6cdd5]`}>Pin / cover / note</button>}
        {can && s.tier !== '0' && s.tier !== 'S' && <button onClick={() => setOpen(open === 'label' ? null : 'label')} className={`${btn} bg-[#dfe3e8] hover:bg-[#c6cdd5]`}>Box label</button>}
        {((r.events?.length || 0) > 0 || (r.history?.length || 0) > 0) && <button onClick={() => setOpen(open === 'log' ? null : 'log')} className="text-sm text-blue-700 underline">{open === 'log' ? 'Hide log' : 'Log'}</button>}
        {msg && <span className="text-xs text-[#374151]">{msg}</span>}
      </div>

      {open === 'sizes' && (
        <div className="mt-2 overflow-x-auto">
          <table className="text-xs w-full">
            <thead className="text-[#5f6368] text-left"><tr><th className="py-1 pr-3">Size</th><th className="py-1 pr-3 text-right">Sold 12 m</th><th className="py-1 pr-3 text-right">Stock</th><th className="py-1 pr-3 text-right">Need</th><th className="py-1 pr-3 text-right">Boxes</th></tr></thead>
            <tbody>
              {(r.sizes || []).map(z => (
                <tr key={z.size_label} className="border-t border-[#e5e7eb]">
                  <td className="py-1 pr-3 font-semibold">{z.size_label}</td><td className="py-1 pr-3 text-right">{z.sold_12m}</td>
                  <td className="py-1 pr-3 text-right">{z.stock_qty}</td><td className="py-1 pr-3 text-right">{z.need}</td><td className="py-1 pr-3 text-right">{z.boxes_need}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[11px] text-[#5f6368] mt-1">Months: {s.months?.from} → · {(s.months?.units || []).join(' ')}</div>
        </div>
      )}

      {open === 'override' && can && (
        <div className="mt-2 border-t border-[#d1d5db] pt-2 space-y-2 text-sm">
          <div className="flex flex-wrap gap-2 items-end">
            <label className="text-xs text-[#5f6368]">Pinned tier<br />
              <select value={pin} onChange={e => setPin(e.target.value)} className={`${input} w-28`}>
                <option value="">— the numbers decide</option>{['A', 'B', 'C', 'J1', 'J2', 'J3'].map(t => <option key={t} value={t}>{t} · {TIER_WORDS[t]}</option>)}
              </select></label>
            <label className="text-xs text-[#5f6368] flex-1 min-w-[12rem]">Reason (required for a pin)<br />
              <input value={reason} onChange={e => setReason(e.target.value)} className={`${input} w-full`} placeholder="why the numbers are wrong for this style" /></label>
            <button disabled={busy || (!!pin && !reason.trim())} onClick={() => patch({ pinned_tier: pin || null, reason })} className={`${btn} bg-blue-600 hover:bg-blue-500 text-white`}>{pin ? 'Pin' : 'Unpin'}</button>
          </div>
          <div className="flex flex-wrap gap-2 items-end">
            <label className="text-xs text-[#5f6368]">Cover months (Henrik's reorder target)<br />
              <input value={cover} onChange={e => setCover(e.target.value)} inputMode="decimal" className={`${input} w-28`} placeholder={String(s.cover_months)} /></label>
            <button disabled={busy} onClick={() => patch({ cover_months_set: cover.trim() ? cover.trim() : null, reason })} className={`${btn} bg-blue-600 hover:bg-blue-500 text-white`}>{cover.trim() ? 'Set cover' : 'Clear cover'}</button>
            <span className="text-xs text-[#5f6368]">now {coverTxt}</span>
          </div>
          <div className="flex flex-wrap gap-2 items-end">
            <label className="text-xs text-[#5f6368] flex-1 min-w-[12rem]">Note<br />
              <input value={note} onChange={e => setNote(e.target.value)} className={`${input} w-full`} placeholder="a note for the next manager" /></label>
            <button disabled={busy || !note.trim()} onClick={() => patch({ note })} className={`${btn} bg-[#dfe3e8] hover:bg-[#c6cdd5]`}>Save note</button>
          </div>
          <div className="text-[11px] text-[#5f6368]">A pinned tier wins over the numbers until unpinned; everything is logged with your name.</div>
        </div>
      )}

      {open === 'label' && can && (
        <div className="mt-2 border-t border-[#d1d5db] pt-2 text-sm">
          <div className="text-xs text-[#5f6368] mb-1">The box label (35×80 roll on the second Zebra): tier letter framed on the left, then</div>
          <div className="border border-[#9aa0a6] rounded p-2 inline-flex items-stretch gap-2 bg-[#fafafa]">
            <div className="border-2 border-[#111111] rounded px-3 flex items-center text-3xl font-bold">{s.tier}</div>
            <div className="text-xs leading-tight">
              <div className="font-semibold">{kindCls || s.family}</div>
              <div>{s.display_sku} {s.name}</div>
              <div>{s.tier.startsWith('J') ? `sized for ${peak} · ${s.waists?.stacks || ''} SL stacks` : `sized for ${peak} · ${s.boxes_should} ${s.box_kind} box${s.boxes_should !== 1 ? 'es' : ''}`}</div>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-2 items-center">
            <label className="text-xs text-[#5f6368]">Copies <input type="number" min={1} max={50} value={copies} onChange={e => setCopies(Math.max(1, Math.min(50, Number(e.target.value) || 1)))} className={`${input} w-16 ml-1`} /></label>
            <button disabled={busy} onClick={printLabel} className={`${btn} bg-blue-600 hover:bg-blue-500 text-white`}>🖨 Print {copies} label{copies !== 1 ? 's' : ''}</button>
            <span className="text-xs text-[#5f6368]">one per box the style should have ({s.boxes_should})</span>
          </div>
        </div>
      )}

      {open === 'log' && (
        <div className="mt-2 border-t border-[#d1d5db] pt-2 text-xs space-y-1">
          {(r.events || []).map(e => <div key={`e${e.id}`}><span className="text-[#5f6368]">{e.at?.slice(0, 16).replace('T', ' ')}</span> · <b>{e.who || '?'}</b> set {e.field.replace(/_/g, ' ')} {e.old_value ?? '—'} → {e.new_value ?? '—'}{e.reason ? ` — "${e.reason}"` : ''}</div>)}
          {(r.history || []).map(h => <div key={`h${h.run_date}`}><span className="text-[#5f6368]">{h.run_date}</span> · tier <b>{h.tier}</b> ({h.tier_source}), {h.boxes_should} boxes, {h.sold_12m} sold, need {h.need_total}, cover {h.cover_months}</div>)}
          {(r.findings || []).map(f => <div key={`f${f.id}`}><span className={f.severity === 'warn' || f.severity === 'contradiction' ? 'text-amber-800' : 'text-[#5f6368]'}>{f.severity}</span> · {FLAG_WORDS[f.kind] || f.kind}{f.text ? `: ${f.text}` : ''}{f.numbers ? ` (${flagNumbers(f.kind, f.numbers).slice(0, 80)})` : ''}</div>)}
        </div>
      )}
    </div>
  );
}
